import { create } from 'zustand';
import Web3 from 'web3';
import { IpcChannel } from '@shared/types/ipc';
import { useWalletEnvStore, MARKETPLACE_ENV } from './wallet-env';
import { isElectron } from '@shared/utils/is-electron';
import { useApiServiceStore } from '@ewf/stores/api';
import { getERC20Balance, getNativeBalance } from '../utils/balance-helpers';

export interface Balance {
  symbol: string;
  token: string;
  base: string;
  // Gas balance (only for ETH network)
  gas?: {
    token: string; // Native ETH balance
    base: string;
    symbol: string; // 'ETH'
  };
}

type BalanceStore = {
  isInitialized: boolean;
  init: () => void;
  cleanUp: () => void;
  isLoading: boolean;
  balanceEWX?: Balance;
  balanceEWC?: Balance;
  balanceETH?: Balance;
  liquidBalance?: string;
  liquidMinBalance?: string;
  usdcBalance?: string;
  intervalId?: NodeJS.Timeout;
  setBalanceEWX: (balance: string | undefined) => void;
  setBalanceEWC: (balance?: Balance) => void;
  setBalanceETH: (balance?: Balance) => void;
  setLiquidBalance: (balance?: string) => void;
  setLiquidMinBalance: (balance?: string) => void;
  setUSDCBalance: (balance?: string) => void;
  formatBalance: (balance: string) => Balance;
  fetchNetworkBalance: (
    web3: Web3,
    isEthNetwork: boolean,
    senderAddress: string,
    tokenAddress?: string,
  ) => Promise<Balance>;
};

export const useBalanceStore = create<BalanceStore>((set, get) => ({
  isInitialized: false,
  init: () => {
    if (get().isInitialized) return;
    set({ isInitialized: true });

    if (isElectron()) {
      window.Api.on(IpcChannel.queryBalance, (data: string) => {
        if (!data) return;
        get().setBalanceEWX(data);
      });
      window.Api.on(IpcChannel.queryLiquidBalance, (data: string) => {
        if (!data) return;
        get().setLiquidBalance(data);
      });
      window.Api.on(IpcChannel.queryLiquidMinBalance, (data: string) => {
        if (!data) return;
        get().setLiquidMinBalance(data);
      });
      window.Api.on(IpcChannel.queryUSDCBalance, (data: string) => {
        if (!data) return;
        get().setUSDCBalance(data);
      });
    } else {
      set({ isLoading: true });
      const workerApi = useApiServiceStore.getState().api;
      workerApi
        .queryBalance()
        .catch((error) => console.error('[BalanceStore] queryBalance error', error))
        .finally(() => set({ isLoading: false }));
      workerApi
        .queryLiquidBalance()
        .catch((error) => console.error('[BalanceStore] queryLiquidBalance error', error))
        .finally(() => set({ isLoading: false }));
      workerApi
        .queryLiquidMinBalance()
        .catch((error) => console.error('[BalanceStore] queryLiquidMinBalance error', error))
        .finally(() => set({ isLoading: false }));
      workerApi
        .queryUSDCBalance()
        .catch((error) => console.error('[BalanceStore] queryUSDCBalance error', error))
        .finally(() => set({ isLoading: false }));

      const TIME_IN_SECONDS = 30;
      const intervalId = setInterval(() => {
        workerApi.queryBalance();
        workerApi.queryLiquidBalance();
        workerApi.queryLiquidMinBalance();
        workerApi.queryUSDCBalance();
      }, TIME_IN_SECONDS * 1000);
      set({ intervalId });
    }
  },
  cleanUp: () => {
    if (isElectron()) {
      window.Api.removeAllListeners(IpcChannel.queryBalance);
      window.Api.removeAllListeners(IpcChannel.queryLiquidBalance);
      window.Api.removeAllListeners(IpcChannel.queryLiquidMinBalance);
      window.Api.removeAllListeners(IpcChannel.queryUSDCBalance);
    } else {
      const intervalId = get().intervalId;
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
    set({ isInitialized: false });
  },
  isLoading: false,
  balanceEWX: undefined,
  balanceEWC: undefined,
  balanceETH: undefined,
  liquidBalance: undefined,
  liquidMinBalance: undefined,
  usdcBalance: undefined,
  intervalId: undefined,
  setBalanceEWX: (balance) => {
    set({ balanceEWX: balance ? get().formatBalance(balance) : undefined });
  },
  setLiquidBalance: (balance) => {
    set({ liquidBalance: balance });
  },
  setLiquidMinBalance: (balance) => {
    set({ liquidMinBalance: balance });
  },
  setBalanceEWC: (balance) => {
    set({ balanceEWC: balance });
  },
  setBalanceETH: (balance) => {
    set({ balanceETH: balance });
  },
  setUSDCBalance: (balance) => {
    set({ usdcBalance: balance });
  },
  formatBalance: (balance: string) => {
    const { env } = useWalletEnvStore.getState();
    const UNIT = env === MARKETPLACE_ENV.PROD ? 'EWT' : 'VT';

    return {
      token: Web3.utils.fromWei(balance, 'ether'),
      base: balance.toString(),
      symbol: UNIT,
    };
  },
  fetchNetworkBalance: async (web3, isEthNetwork, senderAddress, tokenAddress) => {
    const { env } = useWalletEnvStore.getState();
    const UNIT = env === MARKETPLACE_ENV.PROD ? 'EWT' : 'VT';

    if (isEthNetwork && tokenAddress) {
      // ETH Network: Fetch both ERC-20 EWT and native ETH
      const [ewtBalance, ethBalance] = await Promise.all([
        getERC20Balance(web3, tokenAddress, senderAddress), // ERC-20 EWT
        getNativeBalance(web3, senderAddress), // Native ETH for gas
      ]);

      return {
        token: Web3.utils.fromWei(ewtBalance, 'ether'),
        base: ewtBalance,
        symbol: UNIT, // EWT
        gas: {
          token: Web3.utils.fromWei(ethBalance, 'ether'),
          base: ethBalance,
          symbol: 'ETH',
        },
      };
    } else {
      // EWC Network: Only native EWT (which is both token and gas)
      const ewtBalance = await getNativeBalance(web3, senderAddress);
      return {
        token: Web3.utils.fromWei(ewtBalance, 'ether'),
        base: ewtBalance,
        symbol: UNIT, // EWT/VT
      };
    }
  },
}));
